import { useBlockProps, InspectorControls, BlockControls } from "@wordpress/block-editor";
import { PanelBody, SelectControl, TextControl, TextareaControl, Button, Disabled, ToolbarGroup, ToolbarDropdownMenu } from "@wordpress/components";
import DebouncedServerSideRender from "../../../shared/js/DebouncedServerSideRender";
import { __ } from "@wordpress/i18n";
import ProductRefControl from "../../../shared/js/ProductRefControl";

const VARIANT_OPTIONS = [
    { label: __("Default", "content-egg"), value: "default" },
    { label: __("Compact", "content-egg"), value: "compact" },
    { label: __("Summary", "content-egg"), value: "summary" },
];

const COLOR_SCHEME_OPTIONS = [
    { label: __("Auto (site default)", "content-egg"), value: "auto" },
    { label: __("Light", "content-egg"), value: "light" },
    { label: __("Dark", "content-egg"), value: "dark" },
];

export default function Edit({ attributes, setAttributes }) {
    const blockProps = useBlockProps({ className: attributes.align ? `align${attributes.align}` : "" });
    const {
        variant,
        score,
        score_denom,
        score_label,
        title,
        award_label,
        chips = [],
        verdict_text,
        cta_label,
        cta_url,
        product_ref,
        band_label,
        color_scheme,
    } = attributes;

    const updateChips = (nextChips) => setAttributes({ chips: nextChips });
    const addChip = () => updateChips([...(chips || []), ""]);
    const updateChip = (index, value) => {
        const next = [...(chips || [])];
        next[index] = value;
        updateChips(next);
    };
    const removeChip = (index) => {
        const next = [...(chips || [])];
        next.splice(index, 1);
        updateChips(next);
    };

    const hasPreviewContent =
        (score || "").trim() !== "" ||
        (title || "").trim() !== "" ||
        (award_label || "").trim() !== "" ||
        (verdict_text || "").trim() !== "" ||
        (chips || []).some((chip) => (chip || "").trim() !== "");

    return (
        <>
            <BlockControls>
                <ToolbarGroup>
                    <ToolbarDropdownMenu
                        icon="screenoptions"
                        label={__("Variant", "content-egg")}
                        text={VARIANT_OPTIONS.find((o) => o.value === variant)?.label}
                        controls={VARIANT_OPTIONS.map((option) => ({
                            title: option.label,
                            isActive: variant === option.value,
                            onClick: () => setAttributes({ variant: option.value }),
                        }))}
                    />
                </ToolbarGroup>
            </BlockControls>

            <InspectorControls>
                <PanelBody title={__("Layout", "content-egg")} initialOpen={true}>
                    <SelectControl
                        label={__("Variant", "content-egg")}
                        value={variant}
                        options={VARIANT_OPTIONS}
                        onChange={(value) => setAttributes({ variant: value })}
                    />
                    <SelectControl
                        label={__("Color Scheme", "content-egg")}
                        value={color_scheme}
                        options={COLOR_SCHEME_OPTIONS}
                        onChange={(value) => setAttributes({ color_scheme: value })}
                        __nextHasNoMarginBottom
                    />
                </PanelBody>

                <PanelBody title={__("Product Binding", "content-egg")} initialOpen={false}>
                    <ProductRefControl
                        value={product_ref}
                        onChange={(value) => setAttributes({ product_ref: value })}
                    />
                </PanelBody>

                <PanelBody title={__("Content", "content-egg")} initialOpen={true}>
                    <TextControl
                        label={__("Score", "content-egg")}
                        value={score}
                        onChange={(value) => setAttributes({ score: value })}
                    />
                    <TextControl
                        label={__("Score Denominator", "content-egg")}
                        value={score_denom}
                        onChange={(value) => setAttributes({ score_denom: value })}
                    />
                    {variant === "summary" && (
                        <TextControl
                            label={__("Score Label", "content-egg")}
                            value={score_label}
                            onChange={(value) => setAttributes({ score_label: value })}
                        />
                    )}
                    <TextControl
                        label={__("Title", "content-egg")}
                        value={title}
                        onChange={(value) => setAttributes({ title: value })}
                    />
                    <TextControl
                        label={__("Award Label", "content-egg")}
                        value={award_label}
                        onChange={(value) => setAttributes({ award_label: value })}
                    />
                    <TextareaControl
                        label={__("Verdict Text", "content-egg")}
                        value={verdict_text}
                        onChange={(value) => setAttributes({ verdict_text: value })}
                        rows={variant === "compact" ? 3 : 4}
                    />
                    {variant === "summary" && (
                        <TextControl
                            label={__("Band Label", "content-egg")}
                            value={band_label}
                            onChange={(value) => setAttributes({ band_label: value })}
                        />
                    )}
                </PanelBody>

                <PanelBody title={__("Chips", "content-egg")} initialOpen={false}>
                    {(chips || []).map((chip, index) => (
                        <div key={index} style={{ display: "flex", alignItems: "center", gap: "4px", marginBottom: "6px" }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <TextControl
                                    value={chip}
                                    onChange={(value) => updateChip(index, value)}
                                    __nextHasNoMarginBottom
                                />
                            </div>
                            <Button
                                icon="no-alt"
                                isDestructive
                                size="small"
                                onClick={() => removeChip(index)}
                                label={__("Remove", "content-egg")}
                                style={{ flexShrink: 0 }}
                            />
                        </div>
                    ))}
                    <Button variant="secondary" size="small" onClick={addChip} style={{ marginTop: "4px" }}>
                        {__("Add chip", "content-egg")}
                    </Button>
                </PanelBody>

                <PanelBody title={__("CTA", "content-egg")} initialOpen={false}>
                    <TextControl
                        label={__("CTA Label", "content-egg")}
                        value={cta_label}
                        onChange={(value) => setAttributes({ cta_label: value })}
                    />
                    <TextControl
                        label={__("CTA URL", "content-egg")}
                        value={cta_url}
                        onChange={(value) => setAttributes({ cta_url: value })}
                        help={__("Used as a manual fallback when no bound Content Egg product URL is available.", "content-egg")}
                        __nextHasNoMarginBottom
                    />
                </PanelBody>
            </InspectorControls>

            <div {...blockProps}>
                {hasPreviewContent ? (
                    <Disabled>
                        <DebouncedServerSideRender
                            block="eggb/verdict"
                            attributes={attributes}
                        />
                    </Disabled>
                ) : (
                    <p style={{ color: "#999", fontStyle: "italic", padding: "16px", margin: 0, fontSize: "13px" }}>
                        {__("Add verdict content in the sidebar.", "content-egg")}
                    </p>
                )}
            </div>
        </>
    );
}
